import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DocumentItemsFilter } from "src/pages/App/pages/Organization/pages/Document/components/DocumentItemsFilter";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { InvoiceApi, InvoiceItemApi, TaxApi } from "../../../../../../../../../../services/Api/Organization/types";

interface Props {
	invoice: InvoiceApi;
	taxes: TaxApi[];
}

const InvoicePreviewItemsCard = ({ invoice, taxes }: Props) => {
	const [filteredInvoiceItems, setFilteredInvoiceItems] = useState<InvoiceItemApi[]>(invoice.items);
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const currency = organization.currency ?? "";

	const getItemTax = (tax: { id: number; amount: number }) => {
		return taxes.find(({ id }) => tax.id === id);
	};

	return (
		<Card className="card-box positions-card">
			<Card.Body>
				<h5 className="card-title">{t("common.word.items")}</h5>
				<div className="table-responsive">
					<table className="table table-preview">
						<thead>
							<tr>
								<th style={{ width: "30%" }}>{t("lib:common.word.name")}</th>
								<th style={{ width: "10%" }}>{t("modules.invoice.field.pkwiu.title")}</th>
								<th style={{ width: "10%" }}>{t("modules.invoice.field.gtu.title")}</th>
								<th style={{ width: "10%" }}>{t("common.word.volume")}</th>
								<th style={{ width: "10%" }}>{t("common.word.quantity")}</th>
								<th style={{ width: "10%" }}>
									{invoice.calculation_type === "GROSS"
										? t("common.word.unit_price_gross_money")
										: t("common.word.unit_price_net_money")}
								</th>
								<th style={{ width: "10%" }}>
									{invoice.calculation_type === "GROSS"
										? t("modules.invoice.field.price_sum_gross.title")
										: t("modules.invoice.field.price_sum_net.title")}
								</th>
								<th style={{ width: "10%" }}>
									{t("common.word.vat_rate")} <br />
									{t("common.word.vat")}
								</th>
							</tr>
						</thead>
						<tbody>
							<DocumentItemsFilter
								numberOfColumns={10}
								items={invoice.items}
								numberOfFilteredItems={filteredInvoiceItems.length}
								setItems={setFilteredInvoiceItems}
							/>
							{filteredInvoiceItems.map((item) => {
								const indexInOriginalItemsArray = invoice.items.findIndex(
									(originalItem) => originalItem.id === item.id
								);

								return (
									<tr key={item.id}>
										<td>{`${indexInOriginalItemsArray + 1}. ${item.name}`}</td>
										<td>{item.pkwiu}</td>
										<td>{item.gtu}</td>
										<td>
											{`${item.volume} ${
												item.measure_type
													? t(`enums.products.measure_types.${item.measure_type}`)
													: ""
											}`}
										</td>
										<td>{item.quantity}</td>
										<td>
											{invoice.calculation_type === "GROSS"
												? FormatMoney(item.price_gross || undefined)
												: FormatMoney(item.price_net || undefined)}
										</td>
										<td>
											{invoice.calculation_type === "GROSS"
												? FormatMoney(item.price_sum_gross || undefined)
												: FormatMoney(item.price_sum_net || undefined)}
										</td>
										<td>{item.tax ? getItemTax(item.tax)?.name ?? "" : ""}</td>
									</tr>
								);
							})}
							<tr className="summary">
								<td colSpan={4}>
									<div className="d-flex flex-column">
										<span style={{ marginBottom: "30px" }}>
											<strong>{t("lib:common.word.summary")}</strong>
										</span>
										<span>{t("modules.invoice.field.summary_taxes.title")}</span>
									</div>
								</td>
								<td>
									<span className="label mb-2 d-inline-block text-nowrap">
										{t("common.word.value")}
										<br />
										{t("enums.price.type.NET").toLowerCase()}
									</span>
									{invoice.summary.invoice_taxes.map((tax, index) => {
										return (
											<div key={index} className="mb-1 nowrap">
												{FormatMoney(tax.price_net)}
											</div>
										);
									})}
								</td>
								<td>
									<span className="label mb-2 d-inline-block text-nowrap">
										{t("common.word.vat_rate")}
										<br />
										{t("common.word.vat")}
									</span>
									{invoice.summary.invoice_taxes.map((tax, index) => {
										return (
											<div key={index} className="mb-1 nowrap">
												{getItemTax(tax.tax)?.name ?? ""}
											</div>
										);
									})}
								</td>
								<td>
									<span className="label mb-2 d-inline-block text-nowrap">
										{t("modules.invoice.field.sum.title")}
										<br />
										{t("common.word.vat")}
									</span>
									{invoice.summary.invoice_taxes.map((tax, index) => {
										return (
											<div key={index} className="mb-1 nowrap">
												{FormatMoney(tax.price_tax)}
											</div>
										);
									})}
								</td>
								<td>
									<span className="label mb-2 d-inline-block text-nowrap">
										{t("common.word.value")}
										<br />
										{t("enums.price.type.GROSS").toLowerCase()}
									</span>
									{invoice.summary.invoice_taxes.map((tax, index) => {
										return (
											<div key={index} className="mb-1 nowrap">
												{FormatMoney(tax.price_gross)}
											</div>
										);
									})}
								</td>
							</tr>
							<tr className="summary">
								<td className="nowrap pb-3" colSpan={4}>
									<strong>{t("modules.invoice.field.all_together.title")}</strong>
								</td>
								<td className="nowrap pb-3">
									<strong>{FormatMoney(invoice.summary.price_sum_net)}</strong>
								</td>
								<td className="nowrap pb-3" />
								<td className="nowrap pb-3">
									<strong>{FormatMoney(invoice.summary.price_sum_tax)}</strong>
								</td>
								<td className="nowrap pb-3">
									<strong>{FormatMoney(invoice.summary.price_sum_gross)}</strong>
								</td>
							</tr>
							<tr>
								<td colSpan={7} className="nowrap pt-3">
									<div className="d-flex flex-column">
										<span className="d-inline-block mb-1">
											{t("modules.invoice.field.price_paid.title")}
										</span>
										<span>{t("modules.invoice.field.amount_to_pay.title")}</span>
									</div>
								</td>
								<td className="pt-3">
									<div className="d-flex flex-column">
										<span className="d-inline-block mb-1">
											{FormatMoney(invoice.summary.price_paid ?? { amount: 0, currency })}
										</span>
										<span>
											{FormatMoney(invoice.summary.price_to_pay ?? { amount: 0, currency })}
										</span>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</Card.Body>
		</Card>
	);
};

export default InvoicePreviewItemsCard;
